import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRotate,
  faShoppingCart,
  faShieldHalved, 
} from '@fortawesome/free-solid-svg-icons';

const PricingGrid = ({ products, promoDiscount, onCheckout, theme, isInfoOnly = false }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPlanData, setSelectedPlanData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Responsive detection with useEffect to handle SSR and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper functions
  const calculateDiscountedPrice = (basePrice) => {
    return basePrice - (basePrice * promoDiscount / 100);
  };

  const formatPrice = (price, currencyCode) => {
    if (!price) return '0';
    if (currencyCode === 'JPY') {
      return Math.floor(price).toString();
    }
    const formattedPrice = price / 100;
    return formattedPrice % 1 === 0 ? formattedPrice.toFixed(0) : formattedPrice.toFixed(2);
  };

  const getBasePrice = () => {
    const monthlyProduct = products.find(product => product.period === 'Monthly');
    return monthlyProduct?.oneTime?.unit_amount || 1799;
  };

  const calculateSavings = (currentPrice, months, planType) => {
    const basePrice = getBasePrice();
    const totalAtBasePrice = basePrice * months;
    const savings = Math.round(((totalAtBasePrice - currentPrice) / totalAtBasePrice) * 100);
    return savings > 0 ? savings : 0;
  };

  const getMonthsFromPeriod = (period) => {
    const monthsMap = {
      'Monthly': 1,
      'Quarterly': 3,
      'Semi-Annual': 6,
      'Annual': 12
    };
    return monthsMap[period] || 1;
  };

  const getMonthlyRateText = (price, months, period, currencySymbol, planType) => {
    if (period === 'Monthly' && planType === 'onetime') {
      return 'Base price';
    }
    
    const monthlyRate = months === 1 ? price : price / months;
    
    return (
      <>
        <span dangerouslySetInnerHTML={{ __html: currencySymbol }} />
        {formatPrice(monthlyRate, 'USD')}/month
      </>
    );
  };

  const handlePlanSelect = (planId, plan, type) => {
    if (isInfoOnly) return;
    setSelectedPlan(planId);
    setSelectedPlanData({ plan, type });
  };

  const handleCTAClick = () => {
    if (selectedPlanData) {
      onCheckout(selectedPlanData.plan.priceId, selectedPlanData.type);
    }
  };

  const PriceCard = ({ plan, planType, period, isPopular, currencySymbol, showLabel = false }) => {
    if (!plan) {
      return <div className="pricing-empty-card">Not available</div>;
    }

    const originalPrice = plan.unit_amount;
    const discountedPrice = calculateDiscountedPrice(originalPrice);
    const months = getMonthsFromPeriod(period);
    const savings = calculateSavings(discountedPrice, months, planType);
    const planId = `${plan.priceId}_${planType}`;
    const isSelected = selectedPlan === planId;

    const cardClasses = [
      'pricing-card',
      `pricing-card--${planType}`,
      isSelected && !isInfoOnly ? 'pricing-card--selected' : '',
      isInfoOnly ? 'pricing-card--info-only' : ''
    ].filter(Boolean).join(' ');

    return (
      <motion.div
        className={cardClasses}
        onClick={isInfoOnly ? undefined : () => handlePlanSelect(planId, plan, planType === 'subscription' ? 'recurring' : 'one_time')}
        whileHover={!isInfoOnly && !isSelected ? { y: -4 } : {}}
        whileTap={!isInfoOnly ? { scale: 0.98 } : {}}
      >
        {/* Mobile label */}
        {showLabel && (
          <div className={`pricing-card__label pricing-card__label--${planType}`}>
            <FontAwesomeIcon icon={planType === 'subscription' ? faRotate : faShoppingCart} />
            <span>{planType === 'subscription' ? 'Subscription' : 'One-time'}</span>
          </div>
        )}
        
        <div className="pricing-card__price">
          {promoDiscount > 0 && (
            <span className="pricing-card__original-price">
              <span dangerouslySetInnerHTML={{ __html: currencySymbol }} />
              {formatPrice(originalPrice, plan.currencyCode)}
            </span>
          )}
          <span className={promoDiscount > 0 ? 'pricing-card__discounted-price' : ''}>
            <span dangerouslySetInnerHTML={{ __html: currencySymbol }} />
            {formatPrice(discountedPrice, plan.currencyCode)}
          </span>
        </div>

        <div className="pricing-card__monthly-rate">
          {getMonthlyRateText(discountedPrice, months, period, currencySymbol, planType)}
        </div>

        {savings > 0 ? (
          <div className="pricing-card__savings">Save {savings}%</div>
        ) : (
          <div className="pricing-card__savings-placeholder"></div>
        )}

        {isSelected && !isInfoOnly && (
          <div className="pricing-card__check-badge">✓</div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="pricing-grid-container">
      {/* Desktop Header */}
      <div className="pricing-grid__header">
        <div className="pricing-grid__header-spacer"></div>
        
        <div className="pricing-grid__header-cell pricing-grid__header-cell--onetime">
          <div className="pricing-grid__header-title">
            <FontAwesomeIcon icon={faShoppingCart} />
            <span>One-time</span>
          </div>
          <div className="pricing-grid__header-subtitle">
            No commitment • No renewal
          </div>
        </div>
        
        <div className="pricing-grid__header-cell pricing-grid__header-cell--subscription">
          <div className="pricing-grid__header-title">
            <FontAwesomeIcon icon={faRotate} />
            <span>Subscription</span>
          </div>
          <div className="pricing-grid__header-subtitle">
            Best value • Cancel anytime
          </div>
        </div>
      </div>

      {/* Period Rows */}
      {products.map((product, index) => {
        const currencySymbol = product.subscription?.currencySymbolHtml || product.oneTime?.currencySymbolHtml || '$';
        
        return (
          <motion.div
            key={product.period}
            className="pricing-grid__row"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.1 }}
          >
            <div className="pricing-grid__period-label">
              {product.period}
            </div>

            <PriceCard
              plan={product.oneTime}
              planType="onetime"
              period={product.period}
              isPopular={product.isPopular}
              currencySymbol={currencySymbol}
              showLabel={isMobile}
            />
            <PriceCard
              plan={product.subscription}
              planType="subscription"
              period={product.period}
              isPopular={product.isPopular}
              currencySymbol={currencySymbol}
              showLabel={isMobile}
            />
          </motion.div>
        );
      })}

      {/* CTA Section */}
      {!isInfoOnly && (
        <div className="pricing-grid__cta">
          <motion.button
            className={`pricing-grid__cta-button ${!selectedPlan ? 'pricing-grid__cta-button--disabled' : ''}`}
            whileHover={selectedPlan ? { y: -2 } : {}}
            whileTap={selectedPlan ? { scale: 0.95 } : {}}
            disabled={!selectedPlan}
            onClick={handleCTAClick}
          >
            {selectedPlan ? 'Unlock Full Access Now' : 'Select a Plan to Continue'}
          </motion.button>
          {selectedPlanData && (() => {
            const matchedProduct = products.find(p =>
              p.oneTime?.priceId === selectedPlanData.plan.priceId ||
              p.subscription?.priceId === selectedPlanData.plan.priceId
            );
            const period = matchedProduct?.period || '';
            const typeLabel = selectedPlanData.type === 'recurring' ? 'Subscription' : 'One-time';
            const currencySymbol = matchedProduct?.subscription?.currencySymbolHtml || matchedProduct?.oneTime?.currencySymbolHtml || '$';
            const price = formatPrice(calculateDiscountedPrice(selectedPlanData.plan.unit_amount), selectedPlanData.plan.currencyCode);
            return (
              <div className="pricing-grid__selected-summary">
                Selected: {period} {typeLabel} — <span dangerouslySetInnerHTML={{ __html: currencySymbol }} /><strong>{price}</strong>
              </div>
            );
          })()}
        </div>
      )}

      {!isInfoOnly && (
        <div className="pricing-grid__secure-badge">
          <div className="pricing-grid__secure-text">
            <FontAwesomeIcon icon={faShieldHalved} className="pricing-grid__secure-icon" />
            <span>Secure payment powered by</span>
            <span className="pricing-grid__stripe-badge">Stripe</span>
          </div>
        </div>
      )}

      <style>{`
        .pricing-grid-container {
          background: ${theme?.backgrounds?.secondary || '#1a1a2e'};
          border-radius: 16px;
          padding: 24px 16px;
          margin-bottom: 40px;
          width: 100%;
          box-sizing: border-box;
        }

        /* Desktop Header */
        .pricing-grid__header {
          display: none;
        }

        .pricing-grid__header-spacer {
          /* Empty spacer for grid alignment */
        }

        .pricing-grid__header-cell {
          text-align: center;
          font-weight: 700;
          padding: 16px 12px;
          background: ${theme?.backgrounds?.card || '#252542'};
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .pricing-grid__header-cell--onetime {
          border-top: 4px solid ${theme?.primary?.orange?.[500] || '#f97316'};
        }

        .pricing-grid__header-cell--onetime .pricing-grid__header-title {
          color: ${theme?.primary?.orange?.[500] || '#f97316'};
        }

        .pricing-grid__header-cell--subscription {
          border-top: 4px solid ${theme?.primary?.blue?.[500] || '#3b82f6'};
        }

        .pricing-grid__header-cell--subscription .pricing-grid__header-title {
          color: ${theme?.primary?.blue?.[500] || '#3b82f6'};
        }

        .pricing-grid__header-title {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 1.1rem;
          font-weight: 700;
        }

        .pricing-grid__header-subtitle {
          font-size: 0.8rem;
          font-weight: 500;
          color: ${theme?.text?.secondary || '#9ca3af'};
          text-align: center;
          line-height: 1.2;
        }

        /* Period Rows - Mobile First (stacked) */
        .pricing-grid__row {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 20px;
        }

        .pricing-grid__period-label {
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1rem;
          color: ${theme?.text?.primary || '#ffffff'};
          padding: 12px 16px;
          background: ${theme?.backgrounds?.card || '#252542'};
          border-radius: 10px;
          border-left: 4px solid ${theme?.borders?.primary || '#3b82f6'};
          text-align: center;
        }

        /* Price Cards */
        .pricing-card {
          background: ${theme?.backgrounds?.card || '#252542'};
          border-radius: 12px;
          padding: 16px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          border: 2px solid ${theme?.borders?.primary || '#374151'};
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 4px;
        }

        .pricing-card--onetime {
          border-left: 4px solid ${theme?.primary?.orange?.[500] || '#f97316'};
        }

        .pricing-card--subscription {
          border-left: 4px solid ${theme?.primary?.blue?.[500] || '#3b82f6'};
        }

        .pricing-card--info-only {
          cursor: default;
        }

        .pricing-card--selected {
          transform: translateY(-4px) scale(1.03);
          border: 3px solid ${theme?.primary?.blue?.[500] || '#3b82f6'};
          box-shadow: 0 8px 30px ${theme?.primary?.blue?.[500]}50, 0 0 0 1px ${theme?.primary?.blue?.[500]}30;
          background: linear-gradient(135deg, ${theme?.primary?.blue?.[500]}12 0%, ${theme?.backgrounds?.card || '#252542'} 100%);
        }

        .pricing-card__check-badge {
          position: absolute;
          top: -10px;
          right: -10px;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: ${theme?.primary?.blue?.[500] || '#3b82f6'};
          color: #fff;
          font-size: 14px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }

        /* Mobile Labels on Cards */
        .pricing-card__label {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 4px;
          margin-bottom: 8px;
          width: fit-content;
          align-self: center;
        }

        .pricing-card__label--onetime {
          background: ${theme?.primary?.orange?.[500]}20;
          color: ${theme?.primary?.orange?.[500] || '#f97316'};
        }

        .pricing-card__label--subscription {
          background: ${theme?.primary?.blue?.[500]}20;
          color: ${theme?.primary?.blue?.[500] || '#3b82f6'};
        }

        .pricing-card__price {
          font-size: 1.75rem;
          font-weight: 900;
          color: ${theme?.text?.primary || '#ffffff'};
          line-height: 1;
        }

        .pricing-card__original-price {
          text-decoration: line-through;
          color: ${theme?.text?.tertiary || '#6b7280'};
          font-size: 1.25rem;
          font-weight: 600;
          margin-right: 8px;
        }

        .pricing-card__discounted-price {
          color: ${theme?.text?.secondary || '#d1d5db'};
        }

        .pricing-card__monthly-rate {
          font-size: 0.8rem;
          color: ${theme?.text?.secondary || '#9ca3af'};
          font-weight: 600;
        }

        .pricing-card__savings {
          font-size: 0.75rem;
          color: ${theme?.primary?.green?.[500] || '#10b981'};
          font-weight: 600;
          margin-top: 4px;
        }

        .pricing-card__savings-placeholder {
          height: 18px;
        }

        .pricing-empty-card {
          min-height: 100px;
          background: ${theme?.backgrounds?.tertiary || '#1f1f3a'};
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${theme?.text?.tertiary || '#6b7280'};
          font-size: 0.8rem;
        }

        /* CTA Section */
        .pricing-grid__cta {
          text-align: center;
          margin-top: 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .pricing-grid__cta-button {
          background: linear-gradient(135deg, ${theme?.primary?.blue?.[500] || '#3b82f6'} 0%, ${theme?.primary?.blue?.[600] || '#2563eb'} 100%);
          color: ${theme?.text?.inverse || '#ffffff'};
          border: none;
          padding: 14px 32px;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          width: 100%;
          max-width: 300px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .pricing-grid__cta-button--disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pricing-grid__selected-summary {
          margin-top: 10px;
          font-size: 0.85rem;
          color: ${theme?.text?.secondary || '#9ca3af'};
          text-align: center;
        }

        .pricing-grid__selected-summary strong {
          color: ${theme?.text?.primary || '#ffffff'};
        }

        .pricing-grid__secure-badge {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 12px;
          font-size: 0.8rem;
          color: ${theme?.text?.tertiary || '#6b7280'};
        }

        .pricing-grid__secure-text {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .pricing-grid__secure-icon {
          color: ${theme?.primary?.green?.[500] || '#10b981'};
        }

        .pricing-grid__stripe-badge {
          background: #635bff;
          color: white;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 700;
          font-size: 0.7rem;
        }

        /* Tablet (481px - 768px) - 2 column for cards */
        @media (min-width: 481px) {
          .pricing-grid-container {
            padding: 24px;
          }

          .pricing-grid__row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }

          .pricing-grid__period-label {
            grid-column: 1 / -1;
            margin-bottom: 4px;
          }

          .pricing-card__price {
            font-size: 1.5rem;
          }
        }

        /* Desktop (769px+) - Full 3 column grid */
        @media (min-width: 769px) {
          .pricing-grid-container {
            padding: 40px 60px;
          }

          .pricing-grid__header {
            display: grid;
            grid-template-columns: 140px 1fr 1fr;
            gap: 24px;
            margin-bottom: 20px;
          }

          .pricing-grid__row {
            grid-template-columns: 140px 1fr 1fr;
            gap: 24px;
            margin-bottom: 16px;
            align-items: stretch;
          }

          .pricing-grid__period-label {
            grid-column: auto;
            margin-bottom: 0;
            padding: 20px 16px;
            font-size: 1rem;
            min-height: 100px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .pricing-card {
            padding: 20px 16px;
            min-height: 120px;
          }

          .pricing-card__label {
            display: none;
          }

          .pricing-card__price {
            font-size: 2rem;
          }

          .pricing-grid__cta-button {
            padding: 16px 40px;
            font-size: 1.1rem;
            min-width: 320px;
            width: auto;
          }

          .pricing-grid__secure-badge {
            font-size: 0.875rem;
          }

          .pricing-grid__stripe-badge {
            font-size: 0.75rem;
          }
        }

        /* Large Desktop (1024px+) */
        @media (min-width: 1024px) {
          .pricing-grid-container {
            padding: 40px 80px;
          }

          .pricing-grid__header {
            gap: 48px;
          }

          .pricing-grid__row {
            gap: 48px;
          }
        }
      `}</style>
    </div>
  );
};

export default PricingGrid;